/**
 * Canonical Log Schema — TypeScript mirror of the observability contract.
 *
 * Mirrors the Python `CanonicalLogEntry` dataclass and related types defined in
 * `lambda_src/_common/canonical_log.py`. This file is the single source of truth
 * for the frontend regarding the Canonical Log Schema structure.
 *
 * Requirements: 10.1, 10.2, 10.3
 */

/** Severity levels defined by the contract (Req. 10.3). */
export type SeverityLevel = "DEBUG" | "INFO" | "AUDIT" | "WARNING" | "ERROR";

/** Logical log categories (Req. 10.1). */
export type LogType = "execution" | "audit" | "error";

/**
 * Canonical Log Entry — the normative observability contract (Req. 10).
 *
 * Mandatory fields (10.1) and contextual optional fields (10.2).
 * Field names in snake_case (10.6). NEVER includes secrets/credentials/tokens (10.7).
 */
export interface CanonicalLogEntry {
  // --- mandatory (Req. 10.1) ---
  timestamp: string; // ISO 8601 UTC
  level: SeverityLevel; // Req. 10.3
  log_type: LogType;
  component: string; // e.g. "irondome-bff", "iam-accesskeys-scheduled"
  action: string; // short event identifier
  message: string; // human-readable text

  // --- contextual optional (Req. 10.2) ---
  correlation_id?: string;
  guardrail?: string;
  managed_environment?: string; // "np" | "prd"
  account_id?: string;
  operator_name?: string;
  operator_subject?: string;
  operation?: string;
  outcome?: string;
  error_detail?: string;
}

/**
 * Row displayed in the logs table — shape depends on the active tab.
 * `raw` holds the full canonical JSON for the detail dialog (Req. 6.4).
 */
export interface LogRow {
  timestamp: string;
  raw: Record<string, unknown>; // CanonicalLogEntry serialized

  // Projections per tab (populated based on active tab)
  // — Execuções
  action?: string;
  accountId?: string;
  // — Auditoria
  operation?: string;
  operatorName?: string;
  outcome?: string;
  guardrail?: string; // also used in Erros
  // — Erros
  source?: string; // log group / guardrail origin
  message?: string;
}

/** URL tab identifiers for the LogsPage. */
export type LogTab = "execucoes" | "auditoria" | "erros";

/** Maps each UI tab to the corresponding backend `log_type` parameter. */
export const TAB_TO_LOG_TYPE: Record<LogTab, LogType> = {
  execucoes: "execution",
  auditoria: "audit",
  erros: "error",
} as const;

/** Valid tab identifiers (source of truth for parseTab). */
const VALID_TABS: ReadonlySet<string> = new Set<string>(["execucoes", "auditoria", "erros"]);

/**
 * Parse a URL `tab` query parameter into a valid `LogTab`.
 *
 * Returns the corresponding tab iff `tab ∈ {"execucoes","auditoria","erros"}`;
 * otherwise returns the default `"execucoes"`.
 *
 * This is a pure function — no side effects, no I/O.
 *
 * Requirements: 9.3
 */
export function parseTab(tab: string | null | undefined): LogTab {
  if (typeof tab === "string" && VALID_TABS.has(tab)) {
    return tab as LogTab;
  }
  return "execucoes";
}
